import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchWallet } from '../api/wallet';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';

const PAGE_SIZE = 50;

const formatAmount = amount => {
    if (amount > 0) return `+$${amount}`;
    if (amount < 0) return `-$${Math.abs(amount)}`;
    return '$0';
};

const formatDate = dateStr => {
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', ...(sameYear ? {} : { year: '2-digit' }),
    });
};

const WalletPage = () => {
    const [balance, setBalance] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const sentinelRef = useRef(null);
    const loadingRef = useRef(false);

    const loadPage = useCallback(async (cursor) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        const isFirst = !cursor;
        if (isFirst) setLoading(true); else setLoadingMore(true);

        try {
            const data = await fetchWallet({ cursor, limit: PAGE_SIZE });
            setBalance(data.balance);
            setHasMore(data.hasMore);
            setTransactions(prev => isFirst ? data.WalletTransactions : [...prev, ...data.WalletTransactions]);
        } catch (err) {
            if (isFirst) setError('Error fetching wallet');
            toast.error('Error fetching wallet: ' + err.message);
        } finally {
            loadingRef.current = false;
            if (isFirst) setLoading(false); else setLoadingMore(false);
        }
    }, []);

    useEffect(() => { loadPage(null); }, [loadPage]);

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore) return;

        const lastTxID = transactions.length > 0
            ? transactions[transactions.length - 1].transactionID
            : null;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && !loadingRef.current) {
                    loadPage(lastTxID);
                }
            },
            { rootMargin: '400px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, transactions, loadPage]);

    if (loading) return <LoadingSpinner />;
    if (error) return <p className="error">{error}</p>;

    return (
        <div className="wallet-page">
            <div className="wallet-header">
                <h2>Wallet</h2>
                <Link to="/wallet/add-money" className="button">Add Money</Link>
            </div>

            <div className="wallet-balance">
                <span className="balance-label">Balance</span>
                ${balance}
            </div>

            {transactions.length === 0 ? (
                <p>No transactions yet.</p>
            ) : (
                <div className="wallet-tx-list">
                    {transactions.map(tx => {
                        const amountClass = tx.amount > 0 ? 'positive' : tx.amount < 0 ? 'negative' : 'zero';
                        return (
                            <div key={tx.transactionID} className="wallet-tx-row">
                                <span className={`tx-amount ${amountClass}`}>{formatAmount(tx.amount)}</span>
                                <span className="tx-date">{formatDate(tx.created)}</span>
                                <span className="tx-reason">{tx.reason}</span>
                                <span className="tx-balance">${tx.balance}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            <div ref={sentinelRef} className="wallet-tx-loader">
                {loadingMore && 'Loading...'}
            </div>

            {!hasMore && transactions.length > 0 && (
                <div className="wallet-tx-end">All transactions loaded</div>
            )}
        </div>
    );
};

export default WalletPage;
